import { useContext, useMemo } from "react";
import { LayoutContext } from "../context/LayoutContext";
import Button from "./button.jsx";

const range = (start, end) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

const DOTS = "...";

const Pagination = ({ currentPage, totalPageCount, onPageChange, sx }) => {
  const { isDesktopLayout } = useContext(LayoutContext);
  const siblingCount = useMemo(() => (isDesktopLayout ? 3 : 1), [isDesktopLayout]);

  const paginationRange = useMemo(() => {
    const totalPageNumbers = siblingCount + 5;

    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount;
      const leftRange = range(1, leftItemCount);
      return [...leftRange, DOTS, totalPageCount];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount;
      const rightRange = range(totalPageCount - rightItemCount + 1, totalPageCount);
      return [firstPageIndex, DOTS, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
    }

    return range(1, totalPageCount);
  }, [currentPage, totalPageCount, siblingCount]);

  const previousPageAvailable = currentPage > 1;
  const nextPageAvailable = currentPage < totalPageCount;

  return (
    <div
      className={`grid grid-cols-[repeat(auto-fit,minmax(24px,1fr))] grid-rows-1 gap-2 sm:gap-3 overflow-x-auto w-full overscroll-none ${sx ? sx : ""}`}
    >
      <Button
        className={`flex justify-center items-center h-8 sm:h-10 rounded-md shadow-sm text-xs sm:text-sm border border-border-subtle ${
          !previousPageAvailable
            ? "opacity-50 cursor-not-allowed"
            : "hover:shadow-md hover:border-t-primary hover:border-r-primary hover:border-b-primary hover:border-l-primary cursor-pointer"
        }`}
        onClick={() => previousPageAvailable && onPageChange(currentPage - 1)}
        disabled={!previousPageAvailable}
        aria-label="Previous page"
      >
        <svg
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 sm:h-5 sm:w-5 text-text-muted"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      </Button>

      {paginationRange.map((page, index) => {
        const selected = page === currentPage;
        const isPage = page !== DOTS;

        return (
          <Button
            key={index}
            className={`flex justify-center items-center h-8 sm:h-10 rounded shadow-sm text-xs sm:text-sm text-text-body ${
              isPage
                ? selected
                  ? "text-primary border border-primary"
                  : "border cursor-pointer hover:border-primary hover:text-primary"
                : "border border-border-subtle text-text-muted"
            }`}
            onClick={() => isPage && onPageChange(page)}
            disabled={!isPage}
            aria-label={isPage ? `Page ${page}` : "More pages"}
          >
            {page}
          </Button>
        );
      })}

      <Button
        className={`flex justify-center items-center h-8 sm:h-10 rounded-md shadow-sm text-xs sm:text-sm border border-border-subtle ${
          !nextPageAvailable
            ? "opacity-50 cursor-not-allowed"
            : "hover:shadow-md hover:border-t-primary hover:border-r-primary hover:border-b-primary hover:border-l-primary cursor-pointer"
        }`}
        onClick={() => nextPageAvailable && onPageChange(currentPage + 1)}
        disabled={!nextPageAvailable}
        aria-label="Next page"
      >
        <svg
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 sm:h-5 sm:w-5 text-text-muted"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            clipRule="evenodd"
          />
        </svg>
      </Button>
    </div>
  );
};

export default Pagination;
